import { useState, useCallback, useEffect } from 'react';
import { useApi } from './useApi';
import { strategyEngineService } from '../services/strategyEngineService';
import {
    onSignalMessage,
    onSignalStatusChange,
    isSignalWsConnected,
} from '../ws/signalWsClient';

// Signal history (REST) + live prepend via the global signal WebSocket
// (ws/signalWsClient.js, opened in App.js). Exposes wsConnected for the dot.
export const useSignals = () => {
    const { loading, error, executeRequest, clearError } = useApi();
    const [signals, setSignals] = useState([]);
    const [total, setTotal] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [wsConnected, setWsConnected] = useState(isSignalWsConnected);

    // ── REST: paginated signal history ──────────────────────────────
    const fetchSignals = useCallback(async (page = 1, limit = 20, filters = {}) => {
        try {
            const response = await executeRequest(strategyEngineService.getSignals, page, limit, filters);
            setSignals(response.data || []);
            setTotal(response.meta?.total || 0);
            setIsAdmin(!!response.meta?.isAdmin);
            return response;
        } catch (err) {
            console.error('Failed to fetch signals:', err);
            throw err;
        }
    }, [executeRequest]);

    const fetchSignalsByStrategy = useCallback(async (strategyId, page = 1, limit = 20) => {
        try {
            const response = await executeRequest(strategyEngineService.getSignalsByStrategy, strategyId, page, limit);
            return response;
        } catch (err) {
            console.error('Failed to fetch signals by strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    // ── Live updates from the global signal WebSocket ───────────────
    useEffect(() => {
        // Prepend new signals to the in-page list so the user sees
        // them immediately without a manual refresh.
        const unsubMsg = onSignalMessage((msg) => {
            if (msg?.type === 'NEW_SIGNAL' && msg?.data) {
                setSignals((prev) => [msg.data, ...prev]);
                setTotal((prev) => prev + 1);
            }
        });

        // Mirror the connection status for the green/red indicator dot.
        const unsubStatus = onSignalStatusChange(setWsConnected);

        return () => {
            unsubMsg();
            unsubStatus();
        };
    }, []);

    return {
        signals,
        total,
        loading,
        error,
        isAdmin,
        wsConnected,
        fetchSignals,
        fetchSignalsByStrategy,
        clearError,
    };
};
